/*
  customsirge_home_awards_social_proof.js
  Purpose:  Decision 0056 §3b — while the desktop marquee runs, an award
            card's VERTICAL offset stops being a function of its nth-child
            POSITION (the CSS padding-top cycle) and becomes a function of
            its CURRENT ON-SCREEN X. The cards therefore travel along a
            fixed up-and-down curve painted in screen space, whose valley
            sits under the centred "1,200+ Reviews / 8.5M+ Cans Sold" stat
            block — so no card can ever slide under the stats, at any point
            of any loop (Shahz, verbatim: the cards must "follow the up and
            down curves so that they never cover the 1,200 reviews and 8.5M
            cans sold").
  Spec:     .claude/knowledge/decisions/0056-homepage-motion-and-awards-v1.1.md §3b
            extraction/figma/sections/home_awards_social_proof/spec.json
            (grid 640 tall, six wrappers pitch 286, rest offsets
            0/80/160/100/40/160, stats centred x 584.5..855.5 y 160..236).

  THE CURVE — offset(dx) = max( wave(dx), guard(|dx|) ), dx = cardCentreX - statsCentreX

    wave(dx)   Catmull-Rom spline, cyclic with period N*PITCH (= one marquee
               copy), through the N drawn rest offsets read straight off the
               CSS cycle. Its control points sit at exactly the x each card
               rests at (dx_i = (i - (N-1)/2) * PITCH), so at t = 0 every
               card evaluates to its own drawn offset and the row is
               pixel-identical to the Figma frame. This is the "cycles
               gently" option of 0056 §3b — the drawn silhouette
               0/80/160/100/40/160 is NOT symmetric about the viewport
               centre (card 1 rests at 0 and card 6 at 160 at mirrored x),
               so no even function of dx can reproduce it.

    guard(a)   A Tukey window (flat top + raised-cosine taper) centred on the
               stat block:
                   guard = NEED                                   a <= PLATEAU
                   guard = NEED * (1 + cos(PI*(a-PLATEAU)/TAPER))/2
                                                    PLATEAU < a < PLATEAU+TAPER
                   guard = 0                                      a >= PLATEAU+TAPER
               NEED    = statsRect.bottom - gridRect.top + CLEARANCE  (= 100px
                         at 1440: 70px of head/grid overlap + the 30px the
                         decision asks for), measured live every frame.
               PLATEAU = statsWidth/2 + cardWidth/2 + 12  (= 307.75px) — the
                         half-width of the band in which a 320px card can
                         still horizontally overlap the 271px stat block
                         (295.5px) plus 12px of slack.
               TAPER   = 90px.
               So inside the whole danger band the card top is pinned at
               >= statsBottom + 30, and the guard has eased back to 0 by
               |dx| = 397.75 — before card 4 (|dx| = 143 -> guard 100 ==
               its own drawn 100) and card 5 (|dx| = 429 -> guard 0) are
               displaced at rest. Deviation from the decision's suggested
               "eases to 0 at +-720": a 720px half-width raised cosine would
               push drawn cards 4 and 5 down by 45px and 16px and break the
               t=0 Figma match, which 0056 §3b also requires. The guard is
               narrowed to the geometric danger zone and the drawn wave
               carries the silhouette everywhere else.

  Why a rAF driver (0056 §3b option 2) and not CSS offset-path (option 1):
            offset-path/offset-distance would have to REPLACE the track's
            translateX, which is what customsirge_motion.js's marquee kit
            owns (clone, distance/duration custom properties, pause-on-hover,
            the HA-03 seam) — a section-local rewrite of a kit-owned
            mechanism, against decision 0045's one-owner rule, and Safari
            still needs `offset-path: path()` with a literal path that cannot
            be recalculated from a live viewport width. This file keeps the
            kit marquee untouched and only writes `transform: translateY()`
            on each wrap from its measured centre X — 12 rect reads + 12
            transform writes per frame, batched read-then-write, and only
            while the section is on screen.
  Data:     none ambient. Reads its own root's DOM and the CSS padding-top
            cycle; writes only inline `transform` on `.__card-wrap` and the
            `data-customsirge-awards-wave` attribute on `.__grid` (which is
            what the CSS file keys the padding-top: 0 override on).
            Registers ONE component, `home_awards_social_proof`
            (docs/CONVENTIONS.md #4 — JS registers, never auto-runs).
  Owner:    figma-fixer(home_awards_social_proof) v1.1 §3b
  Updated:  2026-09-28 — seamless wrap: syncLoop() measures the row's true
            copy pitch (clone-first-card left - real-first-card left) and
            writes it to --customsirge-home-awards-loop on the track; the CSS
            keyframe distance and the centring margin read it instead of the
            kit's scrollWidth/2 (1733 vs the true 1716). Kit untouched.
  Updated:  2026-09-25
*/
(function () {
  'use strict';

  var NS = 'customsirge-home-awards-social-proof';
  var CLEARANCE = 30; // px the decision asks for between stats bottom and card top
  var PLATEAU_SLACK = 12;
  var TAPER = 90;

  function mod(n, m) {
    return ((n % m) + m) % m;
  }

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  function initAwardsWave(root) {
    var grid = root.querySelector('.' + NS + '__grid');
    var track = root.querySelector('.' + NS + '__row[data-customsirge-marquee-track]');
    var stats = root.querySelector('.' + NS + '__stats');
    if (!grid || !track || !stats) return undefined;

    var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var desktopQuery = window.matchMedia ? window.matchMedia('(min-width: 750px)') : null;

    // Geometry read ONCE from the resting CSS cycle, never from live rects: the
    // marquee may already be mid-loop by the time this runs, and padding-top /
    // margin-right / width are all immune to the track's translateX.
    var rest = [];   // drawn offset per position, in DOM order
    var pitch = 286; // card width + (negative) overlap margin
    var maxRest = 0;
    var n = 0;

    function realWraps() {
      return Array.prototype.filter.call(track.children, function (node) {
        return node.classList && node.classList.contains(NS + '__card-wrap');
      });
    }

    function calibrate() {
      var wraps = realWraps();
      n = wraps.length;
      if (!n) return false;
      rest = [];
      maxRest = 0;
      for (var i = 0; i < n; i++) {
        var cs = window.getComputedStyle(wraps[i]);
        var pad = parseFloat(cs.paddingTop) || 0;
        rest.push(pad);
        if (pad > maxRest) maxRest = pad;
        if (i === 0) {
          var w = parseFloat(cs.width) || wraps[i].offsetWidth || 320;
          var mr = parseFloat(cs.marginRight) || 0;
          if (w + mr > 0) pitch = w + mr;
        }
      }
      return maxRest > 0;
    }

    // Cyclic Catmull-Rom through the drawn rest offsets. Control point i sits at
    // dx = (i - (n-1)/2) * pitch, i.e. exactly where card i rests.
    function waveAt(dx) {
      var p = dx / pitch + (n - 1) / 2;
      var i = Math.floor(p);
      var f = p - i;
      var p0 = rest[mod(i - 1, n)];
      var p1 = rest[mod(i, n)];
      var p2 = rest[mod(i + 1, n)];
      var p3 = rest[mod(i + 2, n)];
      var v =
        0.5 *
        (2 * p1 +
          (-p0 + p2) * f +
          (2 * p0 - 5 * p1 + 4 * p2 - p3) * f * f +
          (-p0 + 3 * p1 - 3 * p2 + p3) * f * f * f);
      return clamp(v, 0, maxRest);
    }

    function guardAt(adx, need, plateau) {
      if (adx <= plateau) return need;
      if (adx >= plateau + TAPER) return 0;
      return (need * (1 + Math.cos(Math.PI * ((adx - plateau) / TAPER)))) / 2;
    }

    // Loop distance = measured pitch between the real row and its clone. Both
    // rects carry the same translateX, so the difference is transform-free.
    var loopPx = 0;
    function syncLoop() {
      var clone = track.querySelector('[data-customsirge-marquee-clone]');
      var first = realWraps()[0];
      var cloneFirst = clone && clone.querySelector('.' + NS + '__card-wrap');
      if (!first || !cloneFirst) return;
      var px = Math.round((cloneFirst.getBoundingClientRect().left - first.getBoundingClientRect().left) * 100) / 100;
      if (px > 0 && px !== loopPx) {
        loopPx = px;
        track.style.setProperty('--customsirge-home-awards-loop', px + 'px');
      }
    }

    var running = false;
    var visible = false;
    var frameId = 0;
    var painted = [];

    function active() {
      if (grid.getAttribute('data-marquee-enabled') !== 'true') return false;
      if (reduceQuery && reduceQuery.matches) return false;
      if (desktopQuery && !desktopQuery.matches) return false;
      return true;
    }

    function clear() {
      grid.removeAttribute('data-customsirge-awards-wave');
      Array.prototype.forEach.call(track.querySelectorAll('.' + NS + '__card-wrap'), function (wrap) {
        wrap.style.transform = '';
      });
      painted = [];
    }

    function paint() {
      frameId = 0;
      if (!running) return;
      if (!loopPx) syncLoop();
      // Clones included on purpose: every card on screen obeys the same curve.
      var wraps = track.querySelectorAll('.' + NS + '__card-wrap');
      var gridTop = grid.getBoundingClientRect().top;
      var sr = stats.getBoundingClientRect();
      var centre = sr.left + sr.width / 2;
      var need = clamp(sr.bottom - gridTop + CLEARANCE, 0, maxRest);

      // READ pass first, WRITE pass second — never interleaved, so the frame
      // costs one forced layout instead of one per card.
      var i;
      var dxs = [];
      var plateau = 0;
      for (i = 0; i < wraps.length; i++) {
        var r = wraps[i].getBoundingClientRect();
        dxs.push(r.left + r.width / 2 - centre);
        if (!plateau) plateau = sr.width / 2 + r.width / 2 + PLATEAU_SLACK;
      }
      for (i = 0; i < wraps.length; i++) {
        var dx = dxs[i];
        var off = Math.max(waveAt(dx), guardAt(Math.abs(dx), need, plateau));
        off = Math.round(clamp(off, 0, maxRest) * 100) / 100;
        if (painted[i] !== off) {
          wraps[i].style.transform = 'translateY(' + off + 'px)';
          painted[i] = off;
        }
      }
      schedule();
    }

    function schedule() {
      if (!running || frameId) return;
      frameId = window.requestAnimationFrame(paint);
    }

    function update() {
      syncLoop();
      var want = active() && visible;
      if (want === running) {
        if (want) schedule();
        return;
      }
      running = want;
      if (want) {
        if (!calibrate()) {
          running = false;
          return;
        }
        grid.setAttribute('data-customsirge-awards-wave', 'on');
        painted = [];
        schedule();
      } else {
        if (frameId) window.cancelAnimationFrame(frameId);
        frameId = 0;
        clear();
      }
    }

    var io = null;
    if (typeof IntersectionObserver === 'function') {
      io = new IntersectionObserver(
        function (entries) {
          visible = entries[entries.length - 1].isIntersecting;
          update();
        },
        { rootMargin: '200px 0px' }
      );
      io.observe(root);
    } else {
      visible = true;
    }

    function onResize() {
      loopPx = 0;
      syncLoop();
      if (!running) {
        update();
        return;
      }
      calibrate();
      painted = [];
      schedule();
    }

    var debounced = window.Customsirge && Customsirge.debounce ? Customsirge.debounce(onResize, 200) : onResize;
    window.addEventListener('resize', debounced);
    window.addEventListener('customsirge:relayout', debounced);
    if (desktopQuery && desktopQuery.addEventListener) desktopQuery.addEventListener('change', update);
    if (reduceQuery && reduceQuery.addEventListener) reduceQuery.addEventListener('change', update);

    update();

    return function cleanup() {
      if (io) io.disconnect();
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = 0;
      running = false;
      clear();
      window.removeEventListener('resize', debounced);
      window.removeEventListener('customsirge:relayout', debounced);
      if (desktopQuery && desktopQuery.removeEventListener) desktopQuery.removeEventListener('change', update);
      if (reduceQuery && reduceQuery.removeEventListener) reduceQuery.removeEventListener('change', update);
    };
  }

  Customsirge.register('home_awards_social_proof', {
    selector: '[data-customsirge-home-awards-social-proof]',
    init: function (root) {
      var teardown = initAwardsWave(root);
      return function cleanup() {
        if (typeof teardown === 'function') teardown();
      };
    },
  });
})();
